import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Loader2 } from "lucide-react";
import api from "@/lib/api";
import { useUser } from "@/lib/userContext";
import { toast } from "sonner";

/**
 * Handles Emergent OAuth redirect with #session_id=...
 * Exchanges session_id for a backend session cookie, then routes based on:
 *   - sessionStorage['post_login_redirect'] (user-initiated sign-in returns to original page)
 *   - is_admin → /admin
 *   - else → /my (user dashboard)
 *
 * REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
 */
export default function AuthCallback() {
    const navigate = useNavigate();
    const { refresh } = useUser();
    const [error, setError] = useState(null);
    const processed = useRef(false);

    useEffect(() => {
        if (processed.current) return;
        processed.current = true;

        const hash = window.location.hash || "";
        const m = hash.match(/session_id=([^&]+)/);
        if (!m) {
            setError("No session_id found");
            return;
        }
        const sessionId = decodeURIComponent(m[1]);
        (async () => {
            try {
                const { data } = await api.post("/auth/session", { session_id: sessionId });
                await refresh();
                toast.success(`Welcome, ${data.name?.split(" ")[0] || "there"}`);

                let returnTo = null;
                try {
                    returnTo = sessionStorage.getItem("post_login_redirect");
                    sessionStorage.removeItem("post_login_redirect");
                } catch {}

                if (returnTo && returnTo !== "/auth/callback") {
                    navigate(returnTo, { replace: true });
                } else if (data.is_admin) {
                    navigate("/admin", { replace: true });
                } else {
                    navigate("/my", { replace: true });
                }
            } catch (err) {
                console.error(err);
                setError("Sign-in failed. Please try again.");
            }
        })();
    }, [navigate, refresh]);

    return (
        <div className="min-h-screen grid place-items-center bg-black text-white" data-testid="auth-callback">
            <div className="text-center">
                {!error ? (
                    <>
                        <Loader2 className="animate-spin text-saffron mx-auto mb-4" size={28} />
                        <div className="eyebrow">Signing you in...</div>
                    </>
                ) : (
                    <>
                        <div className="text-red-400 mb-4">{error}</div>
                        <button onClick={() => navigate("/")} className="btn-secondary">
                            Back to home
                        </button>
                    </>
                )}
            </div>
        </div>
    );
}
